import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * SAB-840 / SAB-853: read and clear a team's NFL autodraft flag.
 *
 * ONE implementation, consumed by BOTH draft rooms. nflDraftRoomChoice sends
 * in_progress/paused drafts to the legacy room (NFLDraftPage) and pending/completed to the
 * new one (NFLDraftRoomV2), so a flag set during a live draft is read in one room and
 * survives into the other. Two copies would drift, and the flag outlives the draft that
 * set it -- prod carries ten teams still flagged from a draft that completed on 2026-08-27.
 *
 * ── WHY THIS EXISTS AT ALL ──────────────────────────────────────────────────
 *
 * The SERVER sets this flag without the user acting: nfl-auto-pick writes
 * auto_draft_enabled = true when a clock lapses and the manager's queue did not cover the
 * pick. Until this hook shipped there was no client anywhere that wrote the column false --
 * the V2 room rendered a Switch hardcoded to `false` whose handler toasted "toggle from the
 * live draft room", and the live draft room had no autodraft code at all. A flagged manager
 * had no way back short of a direct database write.
 *
 * ── enabled IS NULLABLE, DELIBERATELY ───────────────────────────────────────
 *
 * `null` means "we do not know yet" -- before the first read lands, or after a read failed.
 * It is NOT collapsed to false. Rendering "autodraft is off" when the read failed would
 * tell a manager their picks are their own at the exact moment the server may be making
 * them, which is the one thing this UI must never say. Callers render three states.
 *
 * ── THE WRITE IS VERIFIED, NOT ASSUMED ──────────────────────────────────────
 *
 * PostgREST reports a policy-filtered UPDATE as SUCCESS WITH ZERO ROWS, not as an error.
 * So `if (!error)` is not proof the flag changed -- under a policy that does not match, it
 * is exactly what a silent no-op looks like. Every write therefore returns the affected
 * rows via .select() and treats an empty array as a failure. RLS does currently permit this
 * (nfl_teams_update, USING owner_id = auth.uid()), which is precisely why the check has to
 * be here: a future policy change must surface as a visible failure, not as a toast that
 * says "Autodraft off" over a row that never moved.
 */

export type AutoDraftState = boolean | null;

interface UseNFLAutoDraftArgs {
  teamId: string | null;
  /**
   * Re-read whenever this changes. Callers pass the current pick number: the server flips
   * the flag ON as part of making a pick, so a pick advancing is the moment the value can
   * have changed underneath us.
   */
  refreshKey?: unknown;
  /** Surfaced so a failed write can be toasted rather than swallowed. */
  onError?: (message: string) => void;
}

export function useNFLAutoDraft({ teamId, refreshKey, onError }: UseNFLAutoDraftArgs) {
  const [enabled, setEnabled] = useState<AutoDraftState>(null);
  const [saving, setSaving] = useState(false);
  const onErrorRef = useRef(onError);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  const fail = useCallback((msg: string) => {
    console.error('[autodraft]', msg);
    onErrorRef.current?.(msg);
  }, []);

  const load = useCallback(async () => {
    if (!teamId) { setEnabled(null); return; }
    // Cast: src/integrations/supabase/types.ts is a stale generated snapshot covering 40 of
    // prod's 176 tables and does not contain nfl_teams at all. Regenerating is a ~7,900-line
    // diff across the whole app, not a step this change can carry. Kept to this one module
    // rather than spread across both rooms.
    const { data, error } = await (supabase as any)
      .from('nfl_teams')
      .select('auto_draft_enabled')
      .eq('id', teamId)
      .single();
    if (error) {
      // Do NOT fall back to false -- see the header. Leave the last known value in place.
      fail(`flag read failed: ${error.message}`);
      return;
    }
    setEnabled(data?.auto_draft_enabled === true);
  }, [teamId, fail]);

  useEffect(() => { void load(); }, [load, refreshKey]);

  /**
   * Write the flag and prove the row moved. Resolves to the value now in the database, or
   * null if the write could not be confirmed.
   */
  const setAutoDraft = useCallback(async (next: boolean): Promise<AutoDraftState> => {
    if (!teamId || saving) return null;
    setSaving(true);
    try {
      const { data, error } = await (supabase as any)
        .from('nfl_teams')
        .update({ auto_draft_enabled: next })
        .eq('id', teamId)
        .select('id, auto_draft_enabled');

      if (error) throw new Error(error.message);

      // ZERO ROWS IS A FAILURE. An UPDATE the policy filtered out returns here, not above.
      if (!Array.isArray(data) || data.length === 0) {
        throw new Error('the update matched no rows — you may not own this team');
      }

      const applied = data[0]?.auto_draft_enabled === true;
      setEnabled(applied);
      return applied;
    } catch (e) {
      // Resync from the database rather than leaving the UI asserting something we could
      // not confirm.
      fail(`toggle failed: ${(e as Error).message}`);
      await load();
      return null;
    } finally {
      setSaving(false);
    }
  }, [teamId, saving, fail, load]);

  return { enabled, saving, setAutoDraft, reload: load };
}
